import { useQuery } from "@tanstack/react-query";
import { Eye, Filter, Search, X } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { queryString, request } from "../api";
import { useAuth } from "../auth";
import {
  EmptyState,
  ErrorState,
  JSONPreview,
  LoadingState,
  Modal,
  PageHeader,
  Panel,
  StatusPill,
} from "../components";
import { formatTime } from "../format";

type DataKind = "events" | "deliveries" | "operations" | "orders" | "catalog" | "cursors";
type DataRecord = Record<string, unknown>;

const kinds: Array<{ id: DataKind; label: string; singular?: string }> = [
  { id: "events", label: "Events", singular: "event" },
  { id: "deliveries", label: "Deliveries", singular: "delivery" },
  { id: "operations", label: "Operations", singular: "operation" },
  { id: "orders", label: "Orders" },
  { id: "catalog", label: "Catalog" },
  { id: "cursors", label: "Cursors" },
];

function text(record: DataRecord, ...keys: string[]): string {
  const value = keys.map((key) => record[key]).find((candidate) => candidate !== undefined);
  return value === undefined || value === null ? "—" : String(value);
}

function identity(record: DataRecord): string {
  return text(record, "topic", "operation", "name", "ProductRef", "product_ref", "CursorKey", "id", "ID");
}

function timestamp(record: DataRecord): string | undefined {
  const value = ["received_at", "created_at", "updated_at", "FetchedAt", "fetched_at"]
    .map((key) => record[key])
    .find((candidate) => typeof candidate === "string");
  return typeof value === "string" ? value : undefined;
}

export function DataPage() {
  const { session } = useAuth();
  const [params, setParams] = useSearchParams();
  const requestedKind = params.get("kind") as DataKind | null;
  const kind = kinds.some((item) => item.id === requestedKind) ? requestedKind! : "events";
  const [tenant, setTenant] = useState(() => params.get("tenant") ?? "");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DataRecord>();
  const [revealing, setRevealing] = useState(false);
  const filterKey = kind === "events" ? "outcome" : "status";

  const records = useQuery({
    queryKey: ["data", kind, tenant, status],
    queryFn: () => request<DataRecord[]>(
      `/data/${kind}${queryString({ tenant_id: tenant, [filterKey]: status, limit: 200 })}`,
    ),
    refetchInterval: 20_000,
  });

  const visible = useMemo(() => {
    if (!records.data || !search.trim()) return records.data ?? [];
    const needle = search.trim().toLowerCase();
    return records.data.filter((record) => JSON.stringify(record).toLowerCase().includes(needle));
  }, [records.data, search]);

  const definition = kinds.find((item) => item.id === kind)!;
  const revealable = Boolean(definition.singular && selected && session?.user.role !== "viewer");

  return (
    <>
      <PageHeader
        eyebrow="Flow data / redacted by default"
        title="Trace each handoff through Nexus"
        description="Operational metadata is searchable across tenants. Sensitive values stay masked until an audited step-up reveal."
      />

      <div className="tab-bar" role="tablist" aria-label="Data source">
        {kinds.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={kind === item.id}
            className={kind === item.id ? "tab tab--active" : "tab"}
            key={item.id}
            onClick={() => {
              setParams({ kind: item.id });
              setSelected(undefined);
              setStatus("");
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <Panel>
        <div className="filter-bar">
          <label className="search-field">
            <Search size={16} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search loaded records" />
          </label>
          <label className="compact-field">
            <span>Tenant ID</span>
            <input value={tenant} onChange={(event) => setTenant(event.target.value)} placeholder="All tenants" />
          </label>
          {(kind === "events" || kind === "deliveries" || kind === "operations") && (
            <label className="compact-field">
              <span>{kind === "events" ? "Outcome" : "Status"}</span>
              <input value={status} onChange={(event) => setStatus(event.target.value)} placeholder="Any" />
            </label>
          )}
          <span className="filter-count"><Filter size={14} /> {visible.length} records</span>
        </div>

        {records.isLoading && <LoadingState label={`Reading ${definition.label.toLowerCase()}`} />}
        {records.error && <ErrorState error={records.error} />}
        {!records.isLoading && visible.length === 0 && (
          <EmptyState title={`No ${definition.label.toLowerCase()} found`} body="Adjust the filters or wait for new engine activity." />
        )}
        {visible.length > 0 && (
          <div className="record-table" role="table">
            <div className="record-table__head" role="row">
              <span>Record</span><span>Tenant</span><span>State</span><span>Time</span><span />
            </div>
            {visible.map((record, index) => {
              const id = text(record, "id", "ID");
              return (
                <button
                  type="button"
                  className="record-table__row"
                  role="row"
                  key={`${id}-${index}`}
                  onClick={() => setSelected(record)}
                >
                  <span><strong>{identity(record)}</strong><small>{id}</small></span>
                  <code>{text(record, "tenant_id", "TenantID")}</code>
                  <StatusPill value={text(record, "outcome", "status", "Status")} />
                  <time>{formatTime(timestamp(record))}</time>
                  <Eye size={16} />
                </button>
              );
            })}
          </div>
        )}
      </Panel>

      {selected && (
        <aside className="inspector" aria-label="Record inspector">
          <header>
            <div><p className="eyebrow">{definition.label} / record</p><h2>{identity(selected)}</h2></div>
            <button type="button" className="icon-button" onClick={() => setSelected(undefined)} aria-label="Close inspector"><X size={18} /></button>
          </header>
          <JSONPreview value={selected} />
          {revealable && (
            <button type="button" className="button button--danger button--wide" onClick={() => setRevealing(true)}>
              Reveal stored raw data
            </button>
          )}
          {session?.user.role === "viewer" && definition.singular && (
            <p className="field-help">Viewer accounts cannot reveal raw diagnostic data.</p>
          )}
        </aside>
      )}

      {selected && revealing && definition.singular && (
        <RevealModal
          kind={definition.singular}
          id={text(selected, "id", "ID")}
          onClose={() => setRevealing(false)}
        />
      )}
    </>
  );
}

function RevealModal({ kind, id, onClose }: { kind: string; id: string; onClose(): void }) {
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [reason, setReason] = useState("");
  const [raw, setRaw] = useState<unknown>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await request("/auth/step-up", {
        method: "POST",
        body: JSON.stringify({ password, totp_code: totp }),
      });
      setRaw(await request(`/data/${kind}/${encodeURIComponent(id)}/reveal`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Raw data could not be revealed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Audited raw data reveal" onClose={onClose}>
      {raw ? (
        <div className="stack-form">
          <div className="inline-warning">This view may contain customer or provider data. It is never cached by Nexus.</div>
          <JSONPreview value={raw} />
          <button type="button" className="button button--secondary" onClick={onClose}>Close reveal</button>
        </div>
      ) : (
        <form className="stack-form" onSubmit={(event) => void submit(event)}>
          <p>Re-enter your credentials. The record, operator, reason, and time will be written to the audit trail.</p>
          <label>Password<input type="password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <label>Authenticator code<input inputMode="numeric" required value={totp} onChange={(event) => setTotp(event.target.value)} /></label>
          <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          {error && <div className="inline-error">{error}</div>}
          <div className="form-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="button button--danger" disabled={busy}>{busy ? "Verifying…" : "Reveal raw data"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}
